<template>
  <div class="d-flex flex-column align-center">
    <div style="max-width: 500px; width: 100%">
      <template v-if="step === 'payment'">
        <h4 class="mb-6">Forma de Pagamento</h4>
        <v-alert
          v-for="item in filteredPaymentMethods"
          :key="item.id"
          text
          @click="selectedPaymentMethod = item.id"
          :color="selectedPaymentMethod === item.id ? 'primary' : ''"
          style="cursor: pointer"
        >
          <div class="d-flex gap-2 align-center justify-space-between" outlined>
            <v-icon
              :color="selectedPaymentMethod === item.id ? 'primary' : ''"
              large
            >
              {{ item.icon }}
            </v-icon>
            <h5 class="mb-0 lh-3">{{ item.name }}</h5>
            <v-spacer></v-spacer>
            <div
              v-if="item.id !== 'courtesy'"
              class="text-right d-flex flex-column align-end justify-center gap-1"
            >
              <h5 class="mb-0 lh-1">
                {{
                  (
                    taxPerPaymentMethod.total +
                    (taxPerPaymentMethod.fees[item.id]?.amount || 0)
                  ).toFixed(2) | currency
                }}
              </h5>
              <small class="lh-1">
                Taxa:
                {{
                  (taxPerPaymentMethod.fees[item.id]?.amount || 0).toFixed(2)
                    | currency
                }}
              </small>
            </div>
            <v-icon :color="selectedPaymentMethod === item.id ? 'primary' : ''">
              mdi-{{
                selectedPaymentMethod === item.id
                  ? "radiobox-marked"
                  : "radiobox-blank"
              }}
            </v-icon>
          </div>
        </v-alert>
        <v-btn
          block
          large
          depressed
          color="primary"
          class="mt-4"
          @click="confirmPayment"
          :loading="loading"
          :disabled="!selectedPaymentMethod"
        >
          Confirmar Pagamento
        </v-btn>
      </template>
      <template v-else-if="step === 'delivery'">
        <h4>Entrega dos Ingressos</h4>
        <p>A venda foi finalizada com sucesso!</p>

        <v-alert v-if="error" type="error" class="mb-4" dense outlined>
          {{ error }}
        </v-alert>

        <v-row>
          <v-col cols="6">
            <v-card
              rounded="xl"
              :disabled="!!delivering"
              @click="sendMessage"
              outlined
            >
              <v-alert class="pa-0 mb-0" text height="150" color="success">
                <div
                  class="pa-3 d-flex flex-column align-center justify-center gap-3 text-center"
                  style="min-height: calc(150px)"
                >
                  <v-icon
                    v-if="delivering !== 'message'"
                    x-large
                    color="success"
                  >
                    mdi-whatsapp
                  </v-icon>
                  <v-progress-circular
                    v-else
                    indeterminate
                    color="green"
                    size="48"
                  ></v-progress-circular>
                  <h6 class="mb-0">Enviar por WhatsApp</h6>
                </div>
              </v-alert>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card
              depressed
              rounded="xl"
              :disabled="!!delivering || !isConnected"
              @click="printTickets"
              outlined
            >
              <v-alert class="pa-0 mb-0" text height="150" color="info">
                <div
                  class="d-flex flex-column align-center justify-center text-center pa-3 gap-3"
                  style="min-height: calc(150px)"
                >
                  <v-icon v-if="delivering !== 'print'" x-large color="info">
                    mdi-printer
                  </v-icon>
                  <v-progress-circular
                    v-else
                    indeterminate
                    color="info"
                    size="48"
                  ></v-progress-circular>
                  <div>
                    <h6 class="mb-0">Imprimir Ingressos</h6>
                    <p
                      v-if="!isConnected"
                      class="warning--text lh-1 text-12 mb-0"
                    >
                      Verifique a conexão com a impressora
                    </p>
                    <p v-else-if="!!printProgress" class="lh-1 text-12 mb-0">
                      Imprimindo {{ printProgress }} de
                      {{ printTotal }}
                    </p>
                  </div>
                </div>
              </v-alert>
            </v-card>
          </v-col>
        </v-row>
      </template>
      <template v-else-if="step === 'success'">
        <h4 class="mb-4">Venda Finalizada</h4>
        <p>Os ingressos foram entregues com sucesso!</p>

        <v-progress-linear
          :value="(successRemainingTime / successTotalTime) * 100"
          color="primary"
          class="mb-4"
          height="16"
          rounded
        >
          Finalizando em {{ (successRemainingTime / 1000).toFixed(1) }}
          segundos
        </v-progress-linear>
        <v-btn block large depressed color="primary" @click="finish">
          Nova Venda
        </v-btn>
      </template>
    </div>
    <PaymentConfirm
      ref="paymentConfirm"
      :token="token"
      :payment="payment"
      :paymentMethod="
        paymentMethods.find((pm) => pm.id === selectedPaymentMethod) || {}
      "
      :amount="taxPerPaymentMethod.total"
      :fee="taxPerPaymentMethod.fees[selectedPaymentMethod] || { amount: 0 }"
    />
    <TicketPrintModel
      v-if="step === 'delivery'"
      ref="ticketPrintModel"
      :data="data"
    />
  </div>
</template>
<script>
import VIRTUAL_POS from "@/services/staff/virtualPos";
import PaymentConfirm from "./PaymentConfirm.vue";
import TicketPrintModel from "./TicketPrintModel.vue";
import { mapGetters } from "vuex";

export default {
  name: "PaymentConciliate",
  components: {
    PaymentConfirm,
    TicketPrintModel,
  },
  props: {
    token: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    cart: {
      type: Object,
      required: true,
    },
    selectedParty: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: false,
    },
    client: {
      type: Object,
      required: false,
    },
    payment: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      step: "payment",
      selectedPaymentMethod: null,
      loading: false,
      error: null,
      deliveryTotalTime: 30 * 1000,
      deliveryRemainingTime: null,
      deliveryInterval: null,
      paymentMethods: [
        {
          id: "money",
          name: "Dinheiro",
          icon: "mdi-cash",
        },
        {
          id: "pix",
          name: "Pix",
          icon: "mdi-qrcode-scan",
        },
        {
          id: "credit",
          name: "Cartão de Crédito",
          icon: "mdi-credit-card",
        },
        {
          id: "debit",
          name: "Cartão de Débito",
          icon: "mdi-credit-card-outline",
        },
        {
          id: "courtesy",
          name: "Cortesia",
          icon: "mdi-ticket-percent",
        },
      ],
      feeTranslate: {
        MONEY: "money",
        PIX: "pix",
        DEBIT_CARD: "debit",
        CREDIT_CARD: "credit",
        COURTESY: "courtesy",
      },
      printPromisse: null,
      delivering: false,
      printProgress: 0,
      printTotal: 0,
      successTotalTime: 5000,
      successInterval: null,
      successRemainingTime: null,
    };
  },
  methods: {
    async confirmPayment() {
      try {
        this.loading = true;
        await this.$refs.paymentConfirm.confirm();
        this.step = "delivery";
        // this.deliveryRemainingTime = this.deliveryTotalTime;
        // this.deliveryInterval = setInterval(() => {
        //   this.deliveryRemainingTime -= 1000;
        //   if (this.deliveryRemainingTime <= 0) {
        //     clearInterval(this.deliveryInterval);
        //   }
        // }, 1000);
        this.$root.$emit("payment:confirmed", {
          ...this.payment,
          amount:
            this.taxPerPaymentMethod.total +
            (this.taxPerPaymentMethod.fees[this.selectedPaymentMethod]
              ?.amount || 0),
          paymentType:
            Object.entries(this.feeTranslate).find(
              ([value, key]) => key === this.selectedPaymentMethod
            )?.[0] || "UNKNOWN",
          status: "succeeded",
          payedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error confirming payment:", error);
      } finally {
        this.loading = false;
      }
    },
    async sendMessage() {
      try {
        this.loading = true;
        this.delivering = "message";
        await VIRTUAL_POS.getPaymentTickets(this.token, this.payment.id, true);
        this.successProccess();
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        this.loading = false;
      }
    },
    async printTickets() {
      try {
        this.error = null;
        this.loading = true;
        this.delivering = "print";
        const response = await VIRTUAL_POS.getPaymentTickets(
          this.token,
          this.payment.id,
          false
        );
        const { Ticket, ...payment } = response.payment;
        this.printProgress = 0;
        this.printTotal = Ticket.length;
        for (const [index, ticket] of Ticket.entries()) {
          this.printProgress = index + 1;
          const pdfBlob = await this.$refs.ticketPrintModel.generatePDF({
            ticket: {
              ...ticket,
              sequential: index + 1,
              total: Ticket.length,
              Party: this.selectedParty,
              Client: this.client,
              Payment: payment,
            },
          });
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(pdfBlob);
          });
          this.$root.$emit("print", {
            data: base64Data,
            type: "pixel",
            format: "pdf",
            flavor: "base64",
          });
          await this.waitForPrintDone();
        }
        this.successProccess();
      } catch (error) {
        console.error("Error printing tickets:", error);
        this.error = error.message || "Erro ao imprimir os ingressos.";
      } finally {
        this.loading = false;
        this.delivering = false;
        this.printProgress = false;
        this.printTotal = 0;
      }
    },
    successProccess() {
      this.step = "success";
      this.successRemainingTime = this.successTotalTime;
      this.successInterval = setInterval(() => {
        this.successRemainingTime -= 100;
        if (this.successRemainingTime <= 0) {
          clearInterval(this.successInterval);
          this.$emit("restart");
        }
      }, 100);
    },
    finish() {
      clearInterval(this.successInterval);
      this.$emit("restart");
    },
    confirmPrintDone() {
      if (this.printPromisse) {
        this.printPromisse.resolve();
        this.printPromisse = null;
      }
    },
    async waitForPrintDone() {
      const promise = new Promise((resolve, reject) => {
        this.printPromisse = { resolve, reject };
        setTimeout(() => {
          reject("A impressora demorou muito para responder.");
          this.printPromisse = null;
        }, 15000);
      });
      return promise;
    },
  },
  computed: {
    ...mapGetters("qz", ["isConnected"]),
    filteredPaymentMethods() {
      return this.paymentMethods.filter(({ id }) =>
        this.data.session?.allowPaymentMethods?.includes(id)
      );
    },
    taxPerPaymentMethod() {
      var total = 0;
      const fees = Object.values(this.cart).reduce((acc, item) => {
        const fees = item.ticketBlock.fees || {};
        debugger;
        Object.entries(fees).forEach(([feeType, value]) => {
          const methodId = this.feeTranslate[feeType];
          if (methodId) {
            acc[methodId].amount += (value.amount || 0) * item.quantity;
            acc[methodId].installments = value.installments || [];
          }
        });

        total += item.ticketBlock.price * item.quantity;

        return acc;
      }, Object.fromEntries(this.paymentMethods.map(({ id }) => [id, { amount: 0, installments: [] }])));
      return { fees: fees || {}, total };
    },
  },
  watch: {},
  mounted() {
    this.$root.$on("print:done", this.confirmPrintDone);
  },
};
</script>
